import { createModel, RematchDispatch } from '@rematch/core';
import produce from 'immer';
import moment from 'moment';

import { storeName as agentEntity } from '../entities/agent.entity';
import { storeName as featureEntity } from '../entities/feature.entity';
import { storeName as projectChildItemEntity } from '../entities/projectChildItem.entity';
import { storeName as sessionEntity } from '../entities/session.entity';
import { storeName as termOfContractEntity } from '../entities/termOfContract.entity';

import {
  CREATE_SESSION_STEP,
  DEFAULT_PAGINATION,
  STATUS_CHP,
  TERM_OF_CONTRACT_FIELD_TYPE,
} from '../../commons/constants';
import {
  createSession,
  updatePriceSession,
  updateSession,
  updateStatusSession,
} from '../../services/session.service';
import { PriceType, ProjectChildItemTypes } from '../../types';
import { PaginationResponseTypes, PaginationTypes, ResponseTypes } from '../../types/response';
import { SessionTypes } from '../../types/session.type';
import { RootModel } from '../models';
import { ContractCategoryTypes } from '../../types/contractCategory.type';
import { FeatureTypes } from '../../types/feature.type';

export const storeName = 'sessionModel';

type FilterChpType = {
  projectId: string;
  projectItemIds?: string[];
  floorIds?: string[];
  status?: string;
};

interface FilterSession extends FilterChpType {
  agentIds?: string[];
  name?: string;
  projectName?: string;
}

interface ParamsSearch {
  project?: {
    [key: string]: string;
  };
  projectItems?: [{ [key: string]: string }];
  floors?: [{ [key: string]: string }];
  agents?: [{ [key: string]: string }];
  name?: string;
  statuses?: string;
  projectChildItemKeyword?: string;
}

export interface ChpPostObjectsType {
  code: string;
  availableDate?: string;
  featureCodes?: string[];
  terms: {
    code: string;
    value: any;
    type: TERM_OF_CONTRACT_FIELD_TYPE;
  }[];
  price?: { [key: string]: number };
}

type SessionModelTypes = {
  stepActive: number;
  sessionCodes: string[];
  chpCodes: string[];
  loading: boolean;
  sessionPagination: PaginationTypes;
  chpPagination: PaginationTypes;
  filterSession: FilterSession;
  filterChp: FilterChpType;
  selectedChpPostObjects: ChpPostObjectsType[];
  selectedAgentIds: number[];
  sessionCode: string;
  availableDate: string;
  isEdit: boolean;
  name: string;
  commission: number;
  isViewDetail: boolean;

  price?: { [key: string]: number };
  terms?: ContractCategoryTypes[];
  features?: FeatureTypes[];

  // for keep condition search sessions
  paramsSearch: ParamsSearch;
};

const initialState: SessionModelTypes = {
  stepActive: CREATE_SESSION_STEP.SELECT_CHP.index,
  sessionCodes: [],
  chpCodes: [],
  loading: false,
  sessionPagination: DEFAULT_PAGINATION,
  chpPagination: DEFAULT_PAGINATION,
  filterSession: {
    projectId: '',
    projectItemIds: [],
    floorIds: [],
    agentIds: [],
    status: '',
    name: '',
  },
  filterChp: {
    projectId: '',
    projectItemIds: [],
    floorIds: [],
    status: '',
  },
  selectedChpPostObjects: [],
  selectedAgentIds: [],
  sessionCode: '',
  availableDate: '',
  isEdit: false,
  name: '',
  commission: 0,
  isViewDetail: false,
  paramsSearch: {},
};

export const sessionModel: any = createModel<RootModel>()({
  name: storeName,
  state: initialState,
  reducers: {
    resetSession: produce((state) => {
      state.stepActive = initialState.stepActive;
      state.sessionCodes = initialState.sessionCodes;
      state.commission = initialState.commission;
      state.sessionCode = initialState.sessionCode;
      state.availableDate = initialState.availableDate;
      state.isEdit = initialState.isEdit;
      state.chpPagination = initialState.chpPagination;
      state.filterChp = initialState.filterChp;
      state.chpCodes = initialState.chpCodes;
      state.selectedChpPostObjects = initialState.selectedChpPostObjects;
      state.selectedAgentIds = initialState.selectedAgentIds;
      state.name = initialState.name;
      state.isViewDetail = initialState.isViewDetail;
    }),
    setStepActive: produce((state, payload: number) => {
      state.stepActive = payload;
    }),
    setViewDetail: produce((state, payload: number) => {
      state.isViewDetail = payload;
    }),
    setSessionCode: produce((state, payload: string) => {
      if (payload) {
        state.sessionCode = payload;
      } else {
        state.sessionCode = initialState.sessionCode;
      }
    }),
    setAvailableDate: produce((state, payload: string) => {
      state.availableDate = payload;
    }),
    setLoading: produce((state, payload: number) => {
      state.loading = payload;
    }),
    setSessionCodes: produce((state, payload: string[]) => {
      state.sessionCodes = payload;
    }),
    setChpCodes: produce((state, payload: string[]) => {
      state.chpCodes = payload;
    }),
    setSessionPagination: produce((state, payload: PaginationTypes) => {
      state.sessionPagination = payload;
    }),
    setChpPagination: produce((state, payload: PaginationTypes) => {
      state.chpPagination = payload;
    }),
    setSelectedChpPostObjects: produce((state, payload: ChpPostObjectsType[]) => {
      state.selectedChpPostObjects = payload;
    }),
    setFilterChp: produce((state, payload: FilterChpType) => {
      state.filterChp = payload;
    }),
    setFilterSession: produce((state, payload: FilterChpType) => {
      state.filterSession = payload;
    }),
    setAgentIds: produce((state, payload: number[]) => {
      state.selectedAgentIds = payload;
    }),
    setIsEditStatus: produce((state, payload: boolean) => {
      state.isEdit = payload;
    }),
    setName: produce((state, payload: string) => {
      state.name = payload;
    }),
    setCommission: produce((state, payload: number) => {
      state.commission = payload;
    }),
    setSingleChpPostObjects: produce(
      (state: SessionModelTypes, payload: { index: number; data: ChpPostObjectsType }) => {
        const { index, data } = payload;
        state.selectedChpPostObjects[index] = data;
      }
    ),
    setParamsSearch: produce((state, payload: ParamsSearch) => {
      state.paramsSearch = payload;
    }),
  },
  effects: (dispatch: RematchDispatch<any>) => ({
    async reloadListSession(payload, state) {
      const { filterSession, sessionPagination } = state[storeName];
      await this.fetchDataSession({ filter: filterSession, pagination: sessionPagination });
    },
    nextStep(payload, state) {
      this.setStepActive(state[storeName].stepActive + 1);
    },
    prevStep(payload, state) {
      if (state[storeName].stepActive > 0) this.setStepActive(state[storeName].stepActive - 1);
    },

    setEditFieldSession(payload) {
      this.setIsEditStatus(true);
      this.setSessionDetail(payload);
    },
    closeEditFieldSession(payload, state) {
      this.setIsEditStatus(false);
      this.setSessionCode();
    },
    setViewSessionDetail(payload) {
      if (payload) {
        this.setViewDetail(true);
        this.setSessionDetail(payload);
      } else {
        this.setViewDetail(false);
        this.setStepActive(initialState.stepActive);
      }
    },

    saveInfoSession(payload) {
      const { name, productCode, defaultCommission } = payload;
      this.setName(name);
      this.setCommission(defaultCommission);
      this.setSelectedChpPostObjects([{ code: productCode }]);
    },
    async fetchDataSession(payload = {}, state) {
      this.setLoading(true);

      const {
        pagination = state[storeName].sessionPagination,
        filter = state[storeName].filterSession,
      } = payload;

      if (filter !== state[storeName].filterSession) {
        this.setFilterSession(filter);
      }

      const { projectItemIds, floorIds, agentIds } = filter;
      const resp: PaginationResponseTypes<ProjectChildItemTypes[]> = await dispatch[
        sessionEntity
      ].fetchSessions({
        ...pagination,
        ...filter,
        projectItemIds: projectItemIds?.toString(),
        floorIds: floorIds?.toString(),
        agentIds: agentIds?.toString(),
      });
      if (!resp) return;

      const { data } = resp;
      if (data && !!data.content) {
        this.setSessionCodes(data.content.map((i) => i.code));
      }

      this.setSessionPagination((({ content, ...otherProps }) => otherProps)(data));

      this.setLoading(false);
    },
    async fetchDataChp(payload = {}, state) {
      this.setLoading(true);

      const { pagination = state[storeName].chpPagination, filter = state[storeName].filterChp } =
        payload;
      const { projectItemIds, floorIds, statuses = Object.values(STATUS_CHP) } = filter;

      if (filter !== state[storeName].filterChp) {
        this.setFilterChp(filter);
      }

      const resp: PaginationResponseTypes<ProjectChildItemTypes[]> = await dispatch[
        projectChildItemEntity
      ].fetchProjectChildItem({
        ...pagination,
        ...filter,
        statuses,
        projectItemIds: projectItemIds?.toString(),
        floorIds: floorIds?.toString(),
      });
      if (!resp) return;

      const { data } = resp;
      if (data && !!data.content) {
        this.setChpCodes(data.content.map((i) => i.productCode));
      }
      this.setChpPagination((({ content, ...otherProps }) => otherProps)(data));

      this.setLoading(false);
    },

    async setSessionDetail(payload: string) {
      const resp: ResponseTypes<SessionTypes> = await dispatch[sessionEntity].fetchSession(payload);
      if (!resp) return;

      const { data } = resp;

      if (data) {
        const sellers = (data?.sessionSellers || []).map((item) => ({ ...item.seller }));
        if (sellers.length) {
          dispatch[agentEntity].saveIds(sellers);
          this.setAgentIds(sellers.map((item) => item.id));
        }
        if (data?.childItem) dispatch[projectChildItemEntity].saveInventoryCodes([data.childItem]);
        this.setSessionCode(data.code);
        this.setAvailableDate(data.availableDate);
        const selectedChpPostObjects = {
          code: data.childItem.productCode,
          availableDate: data.availableDate,
          featureCodes: data.features.map((f) => f.code),
          terms: data.terms,
          price: data.price,
        };
        this.setName(data.name);
        this.setCommission(data.defaultCommission);

        this.setSelectedChpPostObjects([selectedChpPostObjects]);
        return data;
      }
    },

    async updateStatusSession(payload) {
      try {
        const dataSession = await updateStatusSession(payload);

        if (dataSession?.data) {
          await dispatch[sessionEntity].saves([dataSession.data]);
        }
      } catch (error) {
        console.error(error);
      }
    },

    async updatePriceSession(payload) {
      try {
        const dataSession = await updatePriceSession(payload);

        if (dataSession?.data) {
          await dispatch[sessionEntity].saves([dataSession.data]);
        }
      } catch (error) {
        console.error(error);
      }
    },

    async createSession(_payload, state) {
      const { selectedChpPostObjects, selectedAgentIds, name, commission } = state[storeName];
      const features = state[featureEntity].codes;

      const session = selectedChpPostObjects.map((item: ChpPostObjectsType) => ({
        commission: commission,
        name: name,
        inventoryProductCode: item.code,
        availableDate: item.availableDate,
        featureIds: item.featureCodes.map((code) => features[code].id),
        terms: item.terms.map((t) => {
          let convertValue = t.value;
          if (t.type === TERM_OF_CONTRACT_FIELD_TYPE.CHECK_BOX) {
            convertValue = !!t.value;
          }
          if (t.type === TERM_OF_CONTRACT_FIELD_TYPE.NUMBER) {
            convertValue = +t.value;
          }
          if (t.type === TERM_OF_CONTRACT_FIELD_TYPE.DATE_TIME) {
            convertValue = moment(t.value).toISOString();
          }
          return {
            ...t,
            value: convertValue,
          };
        }),
        price: item.price,
      }));

      const request = {
        session,
        sellerIds: selectedAgentIds,
      };

      // create session
      const newSession = await createSession(request);
      return newSession;
    },

    async updateSession(payload, state) {
      const { id, agentIds } = payload;
      const { name, commission, availableDate, selectedChpPostObjects } = state[storeName];
      const features = state[featureEntity].codes;
      const request = {
        id,
        name,
        commission,
        sellerIds: agentIds,
        availableDate,
        price: selectedChpPostObjects[0]?.price,
        terms: selectedChpPostObjects[0]?.terms,
        featureIds: selectedChpPostObjects[0]?.featureCodes.map((code) => features[code].id),
      };

      const newSession = await updateSession(request);
      return newSession;
    },
    async updateAvailableDate(payload: { code: string; availableDate: moment.Moment }, state) {
      const { code, availableDate } = payload;
      const { selectedChpPostObjects } = state[storeName];
      const newSelectedChpPostObjects = (selectedChpPostObjects || []).map(
        (item: ChpPostObjectsType) => {
          // find chp by code and add availableDate
          if (item.code === code) {
            return { ...item, availableDate: moment(availableDate).toISOString() };
          }
          return item;
        }
      );

      if (newSelectedChpPostObjects.length) {
        this.setSelectedChpPostObjects(newSelectedChpPostObjects);
      }
    },

    async changeFieldPrice(payload: { code: string; price: PriceType }, state) {
      const { code, price } = payload;
      const { selectedChpPostObjects } = state[storeName];

      const newSelectedChpPostObjects = (selectedChpPostObjects || []).map(
        (item: ChpPostObjectsType) => {
          // find by code and edit field currency
          if (item.code === code) {
            return { ...item, price };
          }
          return item;
        }
      );

      // update new array list chp
      if (newSelectedChpPostObjects.length) {
        this.setSelectedChpPostObjects(newSelectedChpPostObjects);
      }
    },
    async updateAllAvailableDate(payload: moment.Moment, state) {
      const { selectedChpPostObjects } = state[storeName];
      //  update availableDate all selected Chp
      const newSelectedChpPostObjects = selectedChpPostObjects.map((item: ChpPostObjectsType) => ({
        ...item,
        availableDate: payload.toISOString(),
      }));

      if (newSelectedChpPostObjects.length) {
        this.setSelectedChpPostObjects(newSelectedChpPostObjects);
      }
    },

    async updateAllFeature(payload: string[], state) {
      const { selectedChpPostObjects } = state[storeName];

      const newSelectedChpPostObjects = (selectedChpPostObjects || []).map((item) => ({
        ...item,
        featureCodes: payload,
      }));
      this.setSelectedChpPostObjects(newSelectedChpPostObjects);
    },
    async updateFeatureByCode(payload: { code: string; featureCodes: string[] }, state) {
      const { selectedChpPostObjects } = state[storeName];

      const newSelectedChpPostObjects = (selectedChpPostObjects || []).map((item) => ({
        ...item,
        featureCodes: item.code === payload.code ? payload?.featureCodes : item?.featureCodes,
      }));
      this.setSelectedChpPostObjects(newSelectedChpPostObjects);
    },
    applyAllTerms(payload, state) {
      const selectedChpPostObjects = [...state[storeName].selectedChpPostObjects];
      const terms = state[termOfContractEntity].codes;
      const newCHP = selectedChpPostObjects.map((item) => {
        return {
          ...item,
          terms: Object.entries(payload).map(([code, value]) => {
            return {
              code,
              value,
              type: terms[code].type,
            };
          }),
        };
      });
      this.setSelectedChpPostObjects(newCHP);
    },
  }),
});

export default sessionModel;
